import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Sacred Types - Embodying the numerology of Fr00t
interface SacredUser {
  id: string;
  username: string;
  spiritualPath: 'seeker' | 'practitioner' | 'teacher' | 'master';
  spiritualLevel: number;
  
  // Personal Information
  birthDate?: string; // ISO date string
  birthName?: string; // Full name at birth
  currentName?: string; // Current name if different
  
  // Numerology Data
  numerology?: {
    username: any;
    fr00tResonance: string;
    personalProfile?: any; // Complete numerology profile
    lifePathNumber?: number;
    expressionNumber?: number;
    soulUrgeNumber?: number;
    personalityNumber?: number;
    maturityNumber?: number;
    personalYearNumber?: number;
  };
  
  createdAt: string;
}

interface SpiritualState {
  // Sacred State
  user: SacredUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  spiritualConnection: boolean;
  
  // Real-time sensor integration
  sensorData: {
    lastUpdate: number | null;
    accelerometer: any | null;
    orientation: any | null;
    movement: any | null;
  };
  
  // Sacred Actions
  setUsername: (username: string) => void;
  setSpiritualPath: (path: 'seeker' | 'practitioner' | 'teacher' | 'master') => void;
  setPersonalInfo: (birthDate: string, birthName: string, currentName?: string) => void;
  updatePersonalNumerology: () => void;
  logout: () => void;
  initializeSpiritualConnection: () => Promise<void>;
  
  // Real-time sensor actions
  updateSensorData: (data: any) => void;
  
  // Sacred Numerology
  calculateNumerology: (text: string) => any;
  getSacredGeometry: (type: string, dimensions: any) => any;
  
  // Sacred Meditation
  startMeditation: (params: any) => any;
  
  // Sacred Oracle
  consultOracle: (question: string, category?: string) => any;
  
  // Sacred Energy Assessment (now with real-time sensor support)
  getEnergyAssessment: (useSensorData?: boolean) => any;
}

// Initialization flag to prevent multiple simultaneous initializations
let isInitializing = false;

// Sacred Numerology Calculator - Pythagorean System (Pure Mathematics)
// Based on ancient Greek mathematician Pythagoras (c. 570-495 BCE)
// "All is number" - Pythagoras
const calculateSacredNumbers = (text: string) => {
  // Pythagorean Letter-Number Correspondence (verified authentic)
  // Based on position in alphabet modulo 9
  const letterValues: { [key: string]: number } = {
    // 1st group: A(1) J(10→1) S(19→1)
    A: 1, J: 1, S: 1,
    // 2nd group: B(2) K(11→2) T(20→2)
    B: 2, K: 2, T: 2,
    // 3rd group: C(3) L(12→3) U(21→3)
    C: 3, L: 3, U: 3,
    // 4th group: D(4) M(13→4) V(22→4)
    D: 4, M: 4, V: 4,
    // 5th group: E(5) N(14→5) W(23→5)
    E: 5, N: 5, W: 5,
    // 6th group: F(6) O(15→6) X(24→6)
    F: 6, O: 6, X: 6,
    // 7th group: G(7) P(16→7) Y(25→7)
    G: 7, P: 7, Y: 7,
    // 8th group: H(8) Q(17→8) Z(26→8)
    H: 8, Q: 8, Z: 8,
    // 9th group: I(9) R(18→9)
    I: 9, R: 9
  };

  // Calculate total value (sum of all letters)
  const letters = text.toUpperCase().split('').filter(char => /[A-Z]/.test(char));
  const letterBreakdown = letters.map(char => ({
    letter: char,
    value: letterValues[char] || 0
  }));
  
  const total = letters.reduce((sum, char) => sum + (letterValues[char] || 0), 0);

  // Reduce to single digit or master number (11, 22, 33)
  // Master numbers have special significance and are not reduced further
  const reduceNumber = (num: number): number => {
    // Preserve master numbers (11, 22, 33, 44)
    if (num === 11 || num === 22 || num === 33 || num === 44) return num;
    // Already single digit
    if (num < 10) return num;
    // Reduce by adding digits
    const digitSum = String(num).split('').reduce((a, b) => a + parseInt(b), 0);
    return reduceNumber(digitSum);
  };

  const reduced = reduceNumber(total);
  const isMasterNumber = reduced === 11 || reduced === 22 || reduced === 33 || reduced === 44;

  // Calculate additional numerological properties
  const vowelSum = text.toUpperCase().split('').filter(char => 'AEIOU'.includes(char))
    .reduce((sum, char) => sum + (letterValues[char] || 0), 0);
  const consonantSum = total - vowelSum;
  
  const soulUrge = reduceNumber(vowelSum); // Inner desires (vowels)
  const personality = reduceNumber(consonantSum); // Outer expression (consonants)
  const lifePath = reduced; // Overall purpose (total reduced)

  return {
    text,
    total,
    reduced,
    isMasterNumber,
    meaning: getNumerologyMeaning(reduced),
    letterBreakdown,
    soulUrge, // What you desire internally
    personality, // How others perceive you
    lifePath, // Your overall path
    vowelSum,
    consonantSum,
    numerologySystem: 'Pythagorean',
    calculation: `${letters.join('+')} = ${letterBreakdown.map(l => l.value).join('+')} = ${total} → ${reduced}`,
    frequency: mapNumberToFrequency(reduced)
  };
};

// Map numerology number to sacred frequency
const mapNumberToFrequency = (number: number): number => {
  const mapping: { [key: number]: number } = {
    1: 396,  // Liberation, new beginnings
    2: 417,  // Change, cooperation
    3: 528,  // Transformation, joy
    4: 639,  // Stability, connection
    5: 741,  // Freedom, expression
    6: 852,  // Harmony, intuition
    7: 963,  // Wisdom, spirituality
    8: 432,  // Power, abundance (natural tuning)
    9: 528,  // Completion, love
    11: 852, // Master - intuition
    22: 963, // Master - manifestation
    33: 639, // Master - compassion
    44: 528  // Master - healing
  };
  return mapping[number] || 432;
};

// Get numerology meaning (integrated with core digit archetypes)
const getNumerologyMeaning = (number: number): string => {
  // Core single-digit archetypes (including 0) – high-level resonance
  const coreDigits: { [key: number]: string } = {
    0: 'Chaos, truth, infinite potential',
    1: 'Light and change – initiation of new cycles',
    2: 'Balance, unity, polarity held in harmony',
    3: 'Dreams taking structure through expression',
    4: 'Faith expressed as grounded harmony and structure',
    5: 'Gratitude in motion – RA, radiant change and experience',
    6: 'Release through breath, heart-centered presence',
    7: 'Awakening and exxpansion of inner wisdom',
    8: 'Layered success – power, integration, manifestation',
    9: 'Shared wisdom, completion offered back to the collective'
  };

  // Master and extended meanings still sit on top of the digit base
  const masterAndExtended: { [key: number]: string } = {
    11: 'Master number – amplified 2: intuitive light and spiritual insight',
    22: 'Master number – amplified 4: master builder and manifestation',
    33: 'Master number – amplified 6: master teacher and compassion',
    44: 'Master number – amplified 8: master healer of material and spiritual layers'
  };

  if (masterAndExtended[number]) return masterAndExtended[number];
  if (coreDigits[number] !== undefined) return coreDigits[number];

  return 'Unknown';
};

// Sacred Oracle responses - Real spiritual wisdom from authentic teachings
const oracleResponses = {
  general: [
    'The universe guides you toward your highest path. Trust the journey.',
    'Sacred geometry reveals itself in perfect timing. Be patient.',
    'Your frequency is aligning with cosmic harmony. Stay centered.',
    'The answer lies within the stillness. Meditate on this question.',
    'Fr00t numerology shows a powerful resonance. Follow your intuition.',
    'As above, so below; as within, so without. The microcosm reflects the macrocosm.',
    'The present moment is all that exists. Past and future are illusions of mind.',
    'What you seek is seeking you. Your intention has already begun manifesting.',
    'Resistance creates suffering. Acceptance brings peace. Flow with what is.',
    'The observer affects the observed. Your consciousness shapes your reality.',
    'Synchronicity is the universe speaking. Pay attention to signs and symbols.',
    'Every ending is a new beginning. Death and rebirth are eternal cycles.',
    'Your vibration attracts your tribe. Like frequencies resonate together.',
    'The golden mean appears in nature because it represents divine proportion.',
    'Silence is the language of God. All else is poor translation.',
    'You are not a drop in the ocean. You are the entire ocean in a drop.',
    'The Tao that can be told is not the eternal Tao. Truth transcends words.',
    'Consciousness is primary; matter is secondary. Mind creates form.',
    'The hermit\'s cave and the marketplace are the same when you know yourself.',
    'Energy follows attention. Where you place your focus, there power flows.',
  ],
  love: [
    'Love flows when you vibrate at the frequency of compassion.',
    'Partnership requires the balance of two souls in harmony.',
    'Open your heart chakra to receive divine love.',
    'True love is not possession but liberation. Set each other free.',
    'The beloved is a mirror reflecting your own divine nature.',
    'Love is not an emotion but a state of being. It requires no object.',
    'To love another fully, you must first love yourself completely.',
    'Sacred union occurs when two wholes meet, not two halves seeking completion.',
    'The heart chakra vibrates at 639 Hz, the frequency of connection and harmony.',
    'In the space between heartbeats, lovers meet in the eternal now.',
    'Attachment brings suffering; unconditional love brings freedom.',
    'Every relationship is a mirror showing you what needs healing within.',
    'The beloved comes when you are complete within yourself.',
    'Love is giving without expectation, receiving without conditions.',
    'Soul mates are not found but recognized through countless lifetimes.',
  ],
  career: [
    'Your spiritual gifts are meant to serve the collective.',
    'Success follows alignment with your true purpose.',
    'The path of mastery requires dedication and balance.',
    'Do what you love, and prosperity will follow. The universe rewards authentic expression.',
    'Your work is a spiritual practice. Bring consciousness to every action.',
    'Service to others is the highest form of self-realization.',
    'When you work with purpose, money becomes a byproduct, not the goal.',
    'The master\'s journey begins with a single mindful step.',
    'Collaboration over competition: we rise by lifting others.',
    'Your greatest gift to the world is becoming who you truly are.',
    'Right livelihood means work that harms none and benefits all.',
    'The universe abhors a vacuum. Release to make space for the new.',
    'Failure is feedback, not defeat. Every master was once a beginner.',
    'Your calling finds you when you quiet the mind and listen deeply.',
    'Integrity in small things creates trust in great things.',
  ],
  spiritual: [
    'Your spiritual level is ascending. Continue your practice.',
    'The void holds infinite potential. Embrace the unknown.',
    'Sacred frequencies are calling you to higher consciousness.',
    'Enlightenment is not a destination but a continuous awakening.',
    'The ego seeks to become spiritual. The soul simply is.',
    'Meditation is not about stopping thoughts but observing without attachment.',
    'Your breath is the bridge between body and spirit. Breathe consciously.',
    'The kingdom of heaven is within you. Seek not outside yourself.',
    'Wisdom is knowing you know nothing. Ignorance is thinking you know everything.',
    'The spiritual path is not linear but cyclical, a spiral of ever-deepening understanding.',
    'Each chakra is a gateway to higher consciousness. Clear them through practice.',
    'The third eye opens when you stop looking and start seeing.',
    'Enlightenment is the space between thoughts, extended infinitely.',
    'You are not the doer; you are awareness witnessing the doing.',
    'The guru appears when the student is ready. Often, life itself is the teacher.',
    'Transcendence comes not from escaping the world but fully embracing it.',
    'The mystic dances where the lover and beloved become one.',
    'Satsang means sitting in truth. Your presence alone transforms.',
    'The eight-fold path: right view, intention, speech, action, livelihood, effort, mindfulness, concentration.',
    'Liberation is recognizing you were never bound. The cage was always open.',
  ],
  creativity: [
    'Creativity is the soul\'s language. Express without judgment.',
    'The muse visits those who show up consistently. Practice is prayer.',
    'Art is making the invisible visible. Channel divine inspiration.',
    'Every act of creation is first an act of destruction of the old.',
    'The creative impulse flows from the void, the source of all possibility.',
    'Perfectionism blocks flow. Release attachment and let creativity pour through.',
    'Your unique expression is a gift the world needs. No one else can create as you do.',
    'The artist is a vessel through which universal consciousness expresses.',
    'Creative blocks arise when ego interferes. Surrender and become the channel.',
    'Sacred geometry underlies all beautiful creation. Study the patterns of nature.',
  ],
  healing: [
    'Healing begins with acceptance of what is, exactly as it is.',
    'The body knows how to heal when we remove obstacles and provide support.',
    'Every illness is a message. Listen to what your body is telling you.',
    'Sound healing works because everything is vibration. Match the frequency of wellness.',
    'Forgiveness is the master healer. Release resentment to free yourself.',
    'The 528 Hz frequency repairs DNA and restores cells to their original blueprint.',
    'Illness often manifests from suppressed emotions. Feel to heal.',
    'The healer within is more powerful than any external remedy.',
    'Energy follows intention. Direct healing light to areas of dis-ease.',
    'Disclaimer: Frequency claims lack scientific validation.',
  ]
};

// Calculate Fr00t Numerology
const calculateFr00tNumerology = () => {
  const fr00t = 'Fr00t';
  const now = new Date();
  const timestamp = now.toISOString();
  
  const hour = now.getHours();
  const hourNumerology = hour > 9 ? 
    hour.toString().split('').reduce((a, b) => a + parseInt(b), 0) : 
    hour;
  
  return {
    fr00t: fr00t,
    currentHour: hour,
    hourNumerology: hourNumerology,
    timestamp: timestamp
  };
};

// Sacred Store Implementation - Fully Client-Side
export const useSpiritualStore = create<SpiritualState>()(
  persist(
    (set, get) => ({
      // Sacred Initial State
      user: null,
      isAuthenticated: false,
      isLoading: false,
      spiritualConnection: true, // Always connected in client-side mode
      
      // Real-time sensor data
      sensorData: {
        lastUpdate: null,
        accelerometer: null,
        orientation: null,
        movement: null
      },
      
      // Update sensor data
      updateSensorData: (data: any) => {
        set({
          sensorData: {
            lastUpdate: Date.now(),
            accelerometer: data.accelerometer || null,
            orientation: data.orientation || null,
            movement: data.movement || null
          }
        });
      },

      // Set Username
      setUsername: (username: string) => {
        const currentUser = get().user;
        const numerology = calculateSacredNumbers(username);
        const fr00tNumerology = calculateSacredNumbers('Fr00t');
        
        // Preserve existing user data if it exists, only update username-related fields
        const user: SacredUser = {
          id: currentUser?.id || `local-${Date.now()}`,
          username,
          spiritualPath: currentUser?.spiritualPath || 'seeker',
          spiritualLevel: currentUser?.spiritualLevel || 1,
          birthDate: currentUser?.birthDate,
          birthName: currentUser?.birthName,
          currentName: currentUser?.currentName,
          numerology: {
            ...currentUser?.numerology,
            username: numerology,
            fr00tResonance: fr00tNumerology.reduced.toString()
          },
          createdAt: currentUser?.createdAt || new Date().toISOString()
        };

        set({
          user,
          isAuthenticated: true,
          spiritualConnection: true
        });
      },

      // Set Spiritual Path
      setSpiritualPath: (path: 'seeker' | 'practitioner' | 'teacher' | 'master') => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: {
              ...currentUser,
              spiritualPath: path
            }
          });
        }
      },

      // Set Personal Information (birth date, birth name, current name)
      setPersonalInfo: (birthDate: string, birthName: string, currentName?: string) => {
        const currentUser = get().user;
        const baseUser: SacredUser = currentUser || {
          id: `local-${Date.now()}`,
          username: currentName || birthName,
          spiritualPath: 'seeker',
          spiritualLevel: 1,
          numerology: {
            username: calculateSacredNumbers(currentName || birthName),
            fr00tResonance: calculateSacredNumbers('Fr00t').reduced.toString()
          },
          createdAt: new Date().toISOString()
        };

        const updatedUser = {
          ...baseUser,
          birthDate,
          birthName,
          currentName: currentName || birthName
        };

        set({
          user: updatedUser,
          isAuthenticated: true,
          spiritualConnection: true
        });

        // Automatically update numerology after setting personal info
        get().updatePersonalNumerology();
      },

      // Update Personal Numerology (recalculate based on current user data)
      updatePersonalNumerology: () => {
        const currentUser = get().user;
        if (!currentUser) return;

        // Only calculate if we have birth data
        if (currentUser.birthDate && currentUser.birthName) {
          try {
            // Import personal numerology functions dynamically
            import('../domains/numerology/calculator').then(({ generateCompleteProfile }) => {
              const profile = generateCompleteProfile(
                currentUser.birthDate!,
                currentUser.birthName!,
                currentUser.currentName
              );

              // Update user with complete numerology profile
              const baseNumerology = currentUser.numerology || {
                username: calculateSacredNumbers(currentUser.username),
                fr00tResonance: 'Calculating...'
              };
              
              set({
                user: {
                  ...currentUser,
                  numerology: {
                    ...baseNumerology,
                    personalProfile: profile,
                    lifePathNumber: profile.lifePath.lifePathNumber,
                    expressionNumber: profile.activeCoreNumbers.expressionNumber,
                    soulUrgeNumber: profile.activeCoreNumbers.soulUrgeNumber,
                    personalityNumber: profile.activeCoreNumbers.personalityNumber,
                    maturityNumber: profile.activeCoreNumbers.maturityNumber,
                    personalYearNumber: profile.currentYear.personalYearNumber
                  }
                }
              });

              console.log('🔢 Personal numerology updated:', {
                lifePath: profile.lifePath.lifePathNumber,
                expression: profile.activeCoreNumbers.expressionNumber,
                personalYear: profile.currentYear.personalYearNumber
              });
            });
          } catch (error) {
            console.error('❌ Failed to update personal numerology:', error);
          }
        }
      },

      // Sacred Logout
      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          spiritualConnection: true
        });
      },

      // Sacred Connection Initialization
      initializeSpiritualConnection: async () => {
        if (isInitializing) {
          console.log('🌀 Sacred initialization already in progress...');
          return;
        }
        
        const currentState = get();
        if (currentState.user) {
          console.log('🌟 Sacred connection already established from local storage');
          set({ isLoading: false, spiritualConnection: true });
          return;
        }
        
        isInitializing = true;
        set({ isLoading: true });
        
        // Simulate initialization
        await new Promise(resolve => setTimeout(resolve, 500));
        
        set({
          isLoading: false,
          spiritualConnection: true
        });
        
        console.log('🌟 Sacred client-side connection established');
        isInitializing = false;
      },

      // Sacred Numerology Calculation
      calculateNumerology: (text: string) => {
        const result = calculateSacredNumbers(text);
        console.log('🔢 Numerology calculated:', result);
        return {
          success: true,
          data: result,
          fr00tResonance: calculateFr00tNumerology()
        };
      },

      // Sacred Geometry Generation - Complete Mathematical Implementation
      getSacredGeometry: (type: string, dimensions: any) => {
        const goldenRatio = 1.618033988749895; // φ (phi)
        const phi = goldenRatio;
        const pi = Math.PI;
        const sqrt2 = Math.sqrt(2);
        const sqrt3 = Math.sqrt(3);
        const sqrt5 = Math.sqrt(5);
        
        const geometries: { [key: string]: any } = {
          'flower-of-life': {
            type: 'Flower of Life',
            circles: 19, // Traditional complete pattern
            radius: dimensions.size || 100,
            sacred: true,
            meaning: 'The fundamental pattern of creation',
            mathematics: {
              centerCircle: 1,
              innerRing: 6, // 6 circles around center
              secondRing: 12, // 12 circles in second ring
              spacing: 'Each circle touches center at 60° intervals',
              frequency: 528, // Hz - Creation frequency
              formula: 'Based on hexagonal packing: n = 1 + 6k(k-1)/2 where k is ring number'
            },
            symbolism: 'Contains the Seed of Life, Egg of Life, and Fruit of Life patterns',
            applications: 'Found in ancient temples worldwide, represents divine creation blueprint'
          },
          'metatrons-cube': {
            type: "Metatron's Cube",
            vertices: 13, // 13 circles
            edges: 78, // Lines connecting all circles
            sacred: true,
            meaning: 'Contains all five Platonic solids',
            mathematics: {
              circles: 13,
              connections: '13 choose 2 = 78 lines',
              platonicSolids: ['Tetrahedron', 'Cube', 'Octahedron', 'Dodecahedron', 'Icosahedron'],
              frequency: 963, // Hz - Crown chakra
              formula: 'Derived from Fruit of Life by connecting circle centers'
            },
            platonicSolids: {
              tetrahedron: { faces: 4, vertices: 4, edges: 6, element: 'Fire' },
              cube: { faces: 6, vertices: 8, edges: 12, element: 'Earth' },
              octahedron: { faces: 8, vertices: 6, edges: 12, element: 'Air' },
              dodecahedron: { faces: 12, vertices: 20, edges: 30, element: 'Ether' },
              icosahedron: { faces: 20, vertices: 12, edges: 30, element: 'Water' }
            },
            symbolism: 'Named after Archangel Metatron, contains all fundamental 3D forms',
            applications: 'Meditation focal point, energy clearing, sacred architecture'
          },
          'sri-yantra': {
            type: 'Sri Yantra',
            triangles: 9, // 5 downward (Shakti) + 4 upward (Shiva)
            sacred: true,
            meaning: 'Union of masculine and feminine divine energies',
            mathematics: {
              downwardTriangles: 5, // Feminine energy (Shakti)
              upwardTriangles: 4, // Masculine energy (Shiva)
              intersections: 43, // Sacred intersection points
              rings: 3, // Outer square, lotus rings
              frequency: 639, // Hz - Connection and harmony
              angles: 'Precise angles creating 43 triangular regions',
              formula: 'Based on bindu (central point) expansion in sacred ratios'
            },
            structure: {
              bindu: 'Central point - source of creation',
              innerTriangle: 'Primary Shakti-Shiva union',
              lotusRings: { inner: 8, outer: 16 }, // 8 and 16 petal lotuses
              bhupura: 'Square outer boundary representing Earth plane'
            },
            symbolism: 'Map of cosmos, tool for meditation, represents divine proportion',
            applications: 'Meditation, manifestation, balancing masculine/feminine energies'
          },
          'vesica-piscis': {
            type: 'Vesica Piscis',
            circles: 2,
            sacred: true,
            meaning: 'Birth gateway, divine feminine, creation portal',
            mathematics: {
              circles: 2, // Two overlapping circles
              overlap: 'Each circle passes through the other\'s center',
              width: 'Radius of circles',
              height: `radius * √3 = radius * ${sqrt3.toFixed(6)}`,
              ratio: `√3:1 = ${sqrt3.toFixed(6)}:1`,
              frequency: 528, // Hz - Love and creation
              formula: 'Width/Height ratio = 1/√3 (sacred ratio)'
            },
            symbolism: 'Found in Christian art (Christ mandorla), represents divine birth',
            contains: 'Foundation of Flower of Life pattern',
            applications: 'Portal work, feminine healing, creative manifestation'
          },
          'golden-spiral': {
            type: 'Golden Spiral',
            sacred: true,
            meaning: 'Growth pattern of nature, divine expansion',
            mathematics: {
              ratio: phi,
              formula: 'Each quarter turn increases by factor of φ (1.618...)',
              sequence: 'Fibonacci: 1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144...',
              convergence: `Fn+1/Fn approaches φ as n → ∞`,
              frequency: 432, // Hz - Natural tuning
              rectangles: 'Golden rectangle subdivision creates spiral'
            },
            fibonacci: {
              formula: 'F(n) = F(n-1) + F(n-2)',
              goldenRatio: `φ = (1 + √5)/2 = ${phi.toFixed(15)}`,
              inverse: `1/φ = φ - 1 = ${(1/phi).toFixed(15)}`
            },
            inNature: 'Nautilus shell, galaxies, hurricanes, flower petals, pinecones',
            symbolism: 'Divine proportion, perfect growth, cosmic expansion',
            applications: 'Design, architecture, art composition, natural harmony'
          },
          'seed-of-life': {
            type: 'Seed of Life',
            circles: 7, // 6 around 1
            sacred: true,
            meaning: 'Foundation of creation, 7 days of genesis',
            mathematics: {
              centerCircle: 1,
              surroundingCircles: 6,
              angles: '60° separation (360°/6)',
              frequency: 396, // Hz - Liberation frequency
              formula: 'Perfect hexagonal symmetry, basis for Flower of Life'
            },
            symbolism: '7 days of creation, chakra system (7 energy centers)',
            contains: 'Foundation for more complex patterns',
            applications: 'Beginning meditations, creation work, new projects'
          },
          'torus': {
            type: 'Torus (Sacred Donut)',
            sacred: true,
            meaning: 'Universal energy flow pattern',
            mathematics: {
              majorRadius: dimensions.major || 5, // R - center to tube center
              minorRadius: dimensions.minor || 2, // r - tube radius
              surfaceArea: `4π²Rr = ${(4 * pi * pi * (dimensions.major || 5) * (dimensions.minor || 2)).toFixed(2)}`,
              volume: `2π²Rr² = ${(2 * pi * pi * (dimensions.major || 5) * Math.pow(dimensions.minor || 2, 2)).toFixed(2)}`,
              frequency: 528, // Hz - Heart-based flow
              formula: 'Parametric: x=(R+r*cos(v))*cos(u), y=(R+r*cos(v))*sin(u), z=r*sin(v)'
            },
            energyFlow: {
              pattern: 'Energy flows in through top, spirals down and out through bottom, returns',
              vortex: 'Creates self-sustaining electromagnetic field',
              scalability: 'Pattern repeats from atoms to galaxies'
            },
            inNature: 'Earth\'s electromagnetic field, human heart field, apple, orange',
            symbolism: 'Self-sustaining life force, infinite recycling, wholeness',
            applications: 'Energy healing, manifestation, understanding universal flow'
          },
          'merkaba': {
            type: 'Merkaba (Light Body Vehicle)',
            sacred: true,
            meaning: 'Interdimensional light vehicle',
            mathematics: {
              tetrahedra: 2, // Two interlocking tetrahedra
              points: 8, // Total vertices
              faces: 8, // Total faces
              starConfiguration: 'One pointing up (masculine), one pointing down (feminine)',
              frequency: 852, // Hz - Spiritual awakening
              ratio: 'Perfect octahedron created by intersection',
              formula: 'Two tetrahedra in counter-rotation creating star tetrahedron'
            },
            rotation: {
              masculine: 'Clockwise from above',
              feminine: 'Counter-clockwise from above',
              speed: '34/21 ratio (Fibonacci) when activated'
            },
            activation: 'Through breath work and specific meditation',
            symbolism: 'Mer=Light, Ka=Spirit, Ba=Body - Vehicle of ascension',
            applications: 'Interdimensional travel, protection, consciousness expansion'
          }
        };

        const result = geometries[type] || {
          type: 'Custom Geometry',
          goldenRatio: phi,
          pi: pi,
          sqrt2: sqrt2,
          sqrt3: sqrt3,
          sqrt5: sqrt5,
          dimensions,
          message: 'Sacred geometry embodies the fundamental patterns of creation'
        };

        return {
          success: true,
          data: result,
          constants: {
            phi: phi,
            pi: pi,
            e: Math.E,
            sqrt2: sqrt2,
            sqrt3: sqrt3,
            sqrt5: sqrt5,
            goldenAngle: 137.5077640500378 // 360/φ² degrees
          }
        };
      },

      // Sacred Meditation - Real Techniques and Practices
      startMeditation: (params: any) => {
        const duration = params.duration || 10;
        const frequency = params.frequency || 528; // Love frequency
        const type = params.type || 'breath-awareness';
        
        // Real meditation techniques
        const meditationTechniques: { [key: string]: any } = {
          'breath-awareness': {
            name: 'Breath Awareness (Anapanasati)',
            technique: '4-7-8 breathing',
            instructions: [
              'Inhale through nose for 4 counts',
              'Hold breath for 7 counts',
              'Exhale through mouth for 8 counts',
              'Repeat, observing the breath without judgment'
            ],
            frequency: 528, // Hz - Heart coherence
            tradition: 'Buddhist meditation, taught by Buddha',
            benefits: 'Calms nervous system, increases awareness, reduces stress',
            chakra: 'Heart chakra (Anahata)'
          },
          'vipassana': {
            name: 'Vipassana (Insight Meditation)',
            technique: 'Body scan with equanimous observation',
            instructions: [
              'Sit in comfortable position, back straight',
              'Scan body from head to toe',
              'Observe sensations without reacting',
              'Note impermanence of all phenomena'
            ],
            frequency: 852, // Hz - Intuition and insight
            tradition: 'Theravada Buddhism, ancient India',
            benefits: 'Develops insight, breaks conditioning, liberates from suffering',
            chakra: 'Third eye (Ajna)'
          },
          'mantra': {
            name: 'Mantra Meditation',
            technique: 'Sacred sound repetition',
            instructions: [
              'Choose a mantra (OM, So-Ham, or personal)',
              'Repeat silently or aloud',
              'Let meaning dissolve into pure vibration',
              'Return to mantra when mind wanders'
            ],
            frequency: 396, // Hz - Liberation
            commonMantras: ['OM (AUM)', 'So-Ham', 'Om Mani Padme Hum', 'Om Namah Shivaya'],
            tradition: 'Vedic tradition, Hinduism, Buddhism',
            benefits: 'Transcends thought, connects with source vibration',
            chakra: 'Crown chakra (Sahasrara)'
          },
          'zazen': {
            name: 'Zazen (Zen Sitting)',
            technique: 'Just sitting, objectless meditation',
            instructions: [
              'Sit in lotus or half-lotus position',
              'Eyes half-open, gaze downward',
              'Count breaths from 1 to 10, repeat',
              'When thoughts arise, simply return to breath'
            ],
            frequency: 963, // Hz - Divine connection
            tradition: 'Zen Buddhism, Japan',
            benefits: 'Direct realization, no-mind state, pure presence',
            chakra: 'Crown chakra'
          },
          'loving-kindness': {
            name: 'Metta (Loving-Kindness)',
            technique: 'Cultivating universal compassion',
            instructions: [
              'Begin with self: "May I be happy, peaceful, free"',
              'Extend to loved ones, then neutral people',
              'Include difficult people and all beings',
              'Rest in boundless loving-kindness'
            ],
            frequency: 639, // Hz - Heart connection
            phrases: [
              'May all beings be happy',
              'May all beings be free from suffering',
              'May all beings know peace',
              'May all beings awaken to their true nature'
            ],
            tradition: 'Theravada Buddhism',
            benefits: 'Opens heart, dissolves anger, cultivates compassion',
            chakra: 'Heart chakra'
          },
          'chakra-meditation': {
            name: 'Chakra Balancing Meditation',
            technique: 'Ascending through energy centers',
            instructions: [
              'Visualize each chakra from root to crown',
              'Breathe light into each center',
              'Chant corresponding bija mantra',
              'Feel energy rising through central channel'
            ],
            frequency: 528, // Hz - Central heart frequency
            chakras: [
              { name: 'Muladhara (Root)', frequency: 396, mantra: 'LAM', color: 'Red' },
              { name: 'Svadhisthana (Sacral)', frequency: 417, mantra: 'VAM', color: 'Orange' },
              { name: 'Manipura (Solar)', frequency: 528, mantra: 'RAM', color: 'Yellow' },
              { name: 'Anahata (Heart)', frequency: 639, mantra: 'YAM', color: 'Green' },
              { name: 'Vishuddha (Throat)', frequency: 741, mantra: 'HAM', color: 'Blue' },
              { name: 'Ajna (Third Eye)', frequency: 852, mantra: 'OM', color: 'Indigo' },
              { name: 'Sahasrara (Crown)', frequency: 963, mantra: 'AH', color: 'Violet' }
            ],
            tradition: 'Kundalini Yoga, Tantra',
            benefits: 'Balances energy system, activates dormant potential',
            chakra: 'All seven'
          },
          'emptiness': {
            name: 'Śūnyatā (Emptiness Meditation)',
            technique: 'Resting in the void',
            instructions: [
              'Sit in stillness, releasing all objects of meditation',
              'Rest in awareness itself, empty of content',
              'Observe the spacious nature of mind',
              'Recognize the void as infinite potential'
            ],
            frequency: 396, // Hz - Liberation, void frequency
            tradition: 'Mahayana Buddhism, Dzogchen',
            benefits: 'Direct recognition of true nature, liberation from concepts',
            chakra: 'Beyond chakras, pure consciousness'
          },
          'sacred-geometry-contemplation': {
            name: 'Sacred Geometry Visualization',
            technique: 'Contemplating divine patterns',
            instructions: [
              'Visualize a sacred geometric form (Flower of Life, Sri Yantra)',
              'Trace its lines with mind\'s eye',
              'Feel the mathematical perfection',
              'Merge consciousness with the pattern'
            ],
            frequency: 432, // Hz - Universal harmony
            patterns: ['Flower of Life', 'Metatron\'s Cube', 'Sri Yantra', 'Golden Spiral'],
            tradition: 'Hermetic traditions, Platonic schools',
            benefits: 'Attunes mind to cosmic order, enhances visualization',
            chakra: 'Third eye'
          }
        };
        
        const technique = meditationTechniques[type] || meditationTechniques['breath-awareness'];
        
        return {
          success: true,
          data: {
            sessionId: `meditation-${Date.now()}`,
            duration,
            frequency,
            started: new Date().toISOString(),
            type,
            technique: technique.name,
            instructions: technique.instructions,
            benefits: technique.benefits,
            tradition: technique.tradition,
            chakra: technique.chakra,
            fullTechniqueData: technique,
            message: `🧘 Sacred ${technique.name} meditation initiated for ${duration} minutes at ${frequency}Hz`
          }
        };
      },

      // Sacred Oracle Consultation
      consultOracle: (question: string, category: string = 'general') => {
        const responses = oracleResponses[category as keyof typeof oracleResponses] || oracleResponses.general;
        const randomResponse = responses[Math.floor(Math.random() * responses.length)];
        
        const questionNumerology = calculateSacredNumbers(question);
        
        return {
          success: true,
          data: {
            question,
            guidance: randomResponse,
            numerology: questionNumerology,
            timestamp: new Date().toISOString(),
            sacredNumber: questionNumerology.reduced,
            meaning: questionNumerology.meaning,
            fr00tResonance: calculateFr00tNumerology()
          }
        };
      },

      // Sacred Energy Assessment - Based on Circadian Rhythms, Biorhythms, and Real-Time Sensors
      getEnergyAssessment: (useSensorData: boolean = false) => {
        const now = new Date();
        const hour = now.getHours();
        const dayOfMonth = now.getDate();
        
        const state = get();
        const sensors = state.sensorData;
        
        // Calculate circadian rhythm influence (scientific basis)
        // Peak physical: 10am-12pm and 2pm-6pm
        // Peak mental: 9am-11am
        // Peak emotional: varies with individual
        // Peak spiritual: dawn (5-7am) and dusk (6-8pm)
        
        const calculatePhysicalEnergy = (): number => {
          // Based on cortisol and body temperature rhythms
          if (hour >= 10 && hour <= 12) return 95; // Morning peak
          if (hour >= 14 && hour <= 18) return 90; // Afternoon peak
          if (hour >= 22 || hour <= 4) return 60; // Sleep cycle
          if (hour >= 5 && hour <= 9) return 75; // Rising
          return 80; // Baseline
        };
        
        const calculateMentalEnergy = (): number => {
          // Based on cognitive performance research
          if (hour >= 9 && hour <= 11) return 95; // Peak focus time
          if (hour >= 14 && hour <= 16) return 70; // Post-lunch dip
          if (hour >= 19 && hour <= 21) return 85; // Evening clarity
          if (hour >= 22 || hour <= 5) return 55; // Low alertness
          return 75;
        };
        
        const calculateEmotionalEnergy = (): number => {
          // Influenced by serotonin and melatonin cycles
          if (hour >= 6 && hour <= 9) return 85; // Morning optimism
          if (hour >= 15 && hour <= 17) return 90; // Afternoon stability
          if (hour >= 22 || hour <= 4) return 65; // Emotional processing during sleep
          return 80;
        };
        
        const calculateSpiritualEnergy = (): number => {
          // Peak during dawn/dusk (liminal times) and numerologically significant hours
          const hourNumerology = hour > 9 ? 
            hour.toString().split('').reduce((a, b) => a + parseInt(b), 0) : hour;
          
          // Dawn (5-7) and dusk (18-20) are spiritually potent
          if ((hour >= 5 && hour <= 7) || (hour >= 18 && hour <= 20)) return 95;
          // Master number hours (11, 22 o'clock in 24h format)
          if (hour === 11 || hour === 22) return 97;
          // Hours that reduce to sacred numbers (3,6,9)
          if (hourNumerology === 3 || hourNumerology === 6 || hourNumerology === 9) return 90;
          // Midnight (00) - void time
          if (hour === 0) return 100;
          return 75;
        };
        
        let energyLevels = {
          physical: calculatePhysicalEnergy(),
          mental: calculateMentalEnergy(),
          emotional: calculateEmotionalEnergy(),
          spiritual: calculateSpiritualEnergy()
        };
        
        // REAL-TIME SENSOR ENHANCEMENT
        // If sensor data is available and recent (within last 5 seconds), use it to enhance accuracy
        if (useSensorData && sensors.lastUpdate && (Date.now() - sensors.lastUpdate < 5000)) {
          console.log('🎯 Using real-time sensor data for energy assessment');
          
          // Physical energy influenced by actual movement
          if (sensors.movement) {
            const movementBoost = sensors.movement.intensity * 30; // 0-30 boost
            energyLevels.physical = Math.min(100, energyLevels.physical + movementBoost);
          }
          
          // Mental energy influenced by device orientation stability
          if (sensors.orientation && sensors.accelerometer) {
            const stability = 1 - Math.min(sensors.accelerometer.magnitude / 10, 1);
            const stabilityBoost = stability * 20; // 0-20 boost for stillness (focus)
            energyLevels.mental = Math.min(100, energyLevels.mental + stabilityBoost);
          }
          
          // Spiritual energy influenced by alignment (vertical orientation)
          if (sensors.orientation) {
            // Vertical alignment (device upright) increases spiritual energy
            const verticalAlignment = Math.cos(sensors.orientation.beta * Math.PI / 180);
            const alignmentBoost = Math.abs(verticalAlignment) * 15;
            energyLevels.spiritual = Math.min(100, energyLevels.spiritual + alignmentBoost);
          }
          
          // Emotional energy influenced by overall harmony
          const emotionalBoost = (energyLevels.physical + energyLevels.mental + energyLevels.spiritual) / 30 - 5;
          energyLevels.emotional = Math.min(100, energyLevels.emotional + emotionalBoost);
        }
        
        const overall = Math.floor(Object.values(energyLevels).reduce((a, b) => a + b, 0) / 4);
        
        // Generate intelligent recommendations based on actual energy levels
        let recommendation = '';
        if (overall >= 90) {
          recommendation = 'Your energy is exceptionally high! Excellent time for creative work, meditation, or manifestation practices.';
        } else if (overall >= 80) {
          recommendation = 'Your energy is well-balanced. Continue your spiritual practices and maintain this harmony.';
        } else if (overall >= 70) {
          recommendation = 'Your energy is good but could be optimized. Consider meditation, movement, or sound healing.';
        } else if (overall >= 60) {
          recommendation = 'Your energy is moderate. Rest, gentle practices, and grounding exercises recommended.';
        } else {
          recommendation = 'Your energy is low. Prioritize rest, sleep, and gentle restorative practices. Honor your body\'s need for renewal.';
        }
        
        // Add time-specific guidance
        if (hour >= 22 || hour <= 5) {
          recommendation += ' This is natural for the sleep cycle—deep rest supports spiritual integration.';
        } else if (hour >= 5 && hour <= 7) {
          recommendation += ' Dawn is a sacred time for meditation and setting intentions.';
        } else if (hour === 0) {
          recommendation += ' Midnight—the void hour—perfect for deep contemplation or dreamwork.';
        }
        
        // Calculate chakra status based on energetic balance
        const calculateChakraStatus = (chakraFreq: number, baseEnergy: number): string => {
          const variance = (Math.sin(dayOfMonth * chakraFreq / 100) + 1) * 15; // Harmonic variation
          const chakraEnergy = baseEnergy + variance;
          if (chakraEnergy >= 95) return 'fully activated';
          if (chakraEnergy >= 85) return 'flowing';
          if (chakraEnergy >= 75) return 'balanced';
          if (chakraEnergy >= 65) return 'opening';
          return 'developing';
        };

        // Calculate chakras with optional sensor data
        let chakrasData = [
              { 
                name: 'Root (Muladhara)', 
                frequency: 396, 
                status: calculateChakraStatus(396, energyLevels.physical),
                element: 'Earth',
                color: 'Red',
                location: 'Base of spine'
              },
              { 
                name: 'Sacral (Svadhisthana)', 
                frequency: 417, 
                status: calculateChakraStatus(417, energyLevels.emotional),
                element: 'Water',
                color: 'Orange',
                location: 'Lower abdomen'
              },
              { 
                name: 'Solar Plexus (Manipura)', 
                frequency: 528, 
                status: calculateChakraStatus(528, energyLevels.physical),
                element: 'Fire',
                color: 'Yellow',
                location: 'Upper abdomen'
              },
              { 
                name: 'Heart (Anahata)', 
                frequency: 639, 
                status: calculateChakraStatus(639, energyLevels.emotional),
                element: 'Air',
                color: 'Green',
                location: 'Center of chest'
              },
              { 
                name: 'Throat (Vishuddha)', 
                frequency: 741, 
                status: calculateChakraStatus(741, energyLevels.mental),
                element: 'Ether',
                color: 'Blue',
                location: 'Throat'
              },
              { 
                name: 'Third Eye (Ajna)', 
                frequency: 852, 
                status: calculateChakraStatus(852, energyLevels.spiritual),
                element: 'Light',
                color: 'Indigo',
                location: 'Between eyebrows'
              },
              { 
                name: 'Crown (Sahasrara)', 
                frequency: 963, 
                status: calculateChakraStatus(963, energyLevels.spiritual),
                element: 'Consciousness',
                color: 'Violet/White',
                location: 'Top of head'
              }
            ];
        
        // SENSOR CHAKRA CALCULATION REMOVED
        // This feature relied on deleted appropriative code that mapped
        // accelerometer axes to Tantric chakras without basis
        if (false && useSensorData && sensors.accelerometer && sensors.orientation) {
          console.log('Sensor chakra calculation disabled');
          const sensorChakras = null as any;
          
          // Update chakra energies with sensor data
          chakrasData = chakrasData.map((chakra, index) => {
            if (sensorChakras[index]) {
              return {
                ...chakra,
                status: sensorChakras[index].status,
                sensorEnhanced: true
              };
            }
            return chakra;
          });
        }
        
        return {
          success: true,
          data: {
            timestamp: now.toISOString(),
            hour,
            hourNumerology: hour > 9 ? hour.toString().split('').reduce((a, b) => a + parseInt(b), 0) : hour,
            energyLevels,
            overall,
            recommendation,
            sensorEnhanced: useSensorData && sensors.lastUpdate !== null,
            optimalTimes: {
              physical: '10am-12pm, 2pm-6pm',
              mental: '9am-11am, 7pm-9pm',
              emotional: '3pm-5pm',
              spiritual: '5am-7am (dawn), 6pm-8pm (dusk), midnight'
            },
            chakras: chakrasData,
            biorhythms: {
              physical: Math.sin((2 * Math.PI * dayOfMonth) / 23) * 50 + 50, // 23-day cycle
              emotional: Math.sin((2 * Math.PI * dayOfMonth) / 28) * 50 + 50, // 28-day cycle
              intellectual: Math.sin((2 * Math.PI * dayOfMonth) / 33) * 50 + 50 // 33-day cycle
            },
            fr00tResonance: calculateFr00tNumerology(),
            scientificBasis: 'Based on circadian rhythm research, biorhythm theory, and traditional energy medicine'
          }
        };
      }
    }),
    {
      name: 'sacred-spiritual-store',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        spiritualConnection: state.spiritualConnection,
        sensorData: state.sensorData
      }),
      version: 1,
      skipHydration: false,
      merge: (persistedState: any, currentState: any) => {
        // Deep merge persisted state with current state
        // Prioritize persisted state to preserve user data across refreshes
        if (!persistedState) {
          return currentState;
        }

        // Merge user data deeply, preserving all nested properties
        let mergedUser = null;
        if (persistedState.user) {
          mergedUser = {
            ...persistedState.user,
            // Preserve numerology completely from persisted state
            numerology: persistedState.user.numerology || currentState.user?.numerology
          };
        } else if (currentState.user) {
          mergedUser = currentState.user;
        }

        return {
          ...currentState,
          // Preserve persisted authentication state
          user: mergedUser,
          isAuthenticated: persistedState.isAuthenticated !== undefined 
            ? persistedState.isAuthenticated 
            : currentState.isAuthenticated,
          spiritualConnection: persistedState.spiritualConnection !== undefined
            ? persistedState.spiritualConnection
            : currentState.spiritualConnection,
          // Merge sensor data, preferring persisted if it exists
          sensorData: persistedState.sensorData || currentState.sensorData
        };
      },
      // Storage configuration - use localStorage with proper error handling
      storage: {
        getItem: (name: string) => {
          try {
            const value = localStorage.getItem(name);
            if (value) {
              return JSON.parse(value);
            }
            return null;
          } catch (error) {
            console.error('Failed to load from localStorage:', error);
            return null;
          }
        },
        setItem: (name: string, value: any) => {
          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch (error) {
            console.error('Failed to save to localStorage:', error);
            // If quota exceeded, try to clear old data
            if (error instanceof DOMException && error.code === 22) {
              console.warn('LocalStorage quota exceeded, attempting cleanup...');
              try {
                // Clear old session data but keep user data
                const keys = Object.keys(localStorage);
                keys.forEach(key => {
                  if (key.startsWith('fr00t_') && !key.includes('user') && !key.includes('sacred')) {
                    localStorage.removeItem(key);
                  }
                });
                // Retry save
                localStorage.setItem(name, JSON.stringify(value));
              } catch (retryError) {
                console.error('Failed to save after cleanup:', retryError);
              }
            }
          }
        },
        removeItem: (name: string) => {
          try {
            localStorage.removeItem(name);
          } catch (error) {
            console.error('Failed to remove from localStorage:', error);
          }
        }
      }
    }
  )
);

// Sacred Export
export default useSpiritualStore;
